import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Info, ExternalLink, Link2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DealSecuritiesSummary } from "./deal-securities-summary";
import { CrossCollateralSummary } from "./cross-collateral-summary";
import { SecurityDetailDrawer } from "../facilities/security-detail-drawer";
import { SecurityFormModal } from "../securities/security-form-modal";
import { getSecurityDisplayName } from "@/lib/security-display-name";
import {
  buildCanonicalDealSecurities,
  type CanonicalDealSecurity,
} from "@/lib/deal-security-register";
import type { Security, Facility } from "@shared/schema";

interface DealSecuritySummary {
  totalValue: number;
  lvrPercentage: number | null;
  securityCount: number;
  facilityCount: number;
  encumbranceCount: number;
  totalEncumbranceValue: number;
}

// Extended security type with facility information
interface DealSecurity extends Security {
  facilityId: string;
  facilityName?: string;
  linkId: string;
  isFirstRanking: boolean | null;
  securityPosition: string | null;
  securedAmount: string | null;
  includeInLvr: boolean | null;
  lvrContributionPercentage: string | null;
  propertyAddress?: string | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleRegistrationNumber?: string | null;
  equipmentMake?: string | null;
  equipmentModel?: string | null;
  equipmentSerialNumber?: string | null;
  equipmentAssetTag?: string | null;
  financialAssetDescription?: string | null;
  financialAssetType?: string | null;
  financialAssetInstitutionName?: string | null;
}

type DealSecurityWithFacility = DealSecurity & {
  facilityId: string;
  facilityName?: string;
};

interface DealSecuritiesTabProps {
  dealId: string;
}

const SECURITY_TYPE_LABELS: Record<string, string> = {
  residential_property: "Residential",
  commercial_property: "Commercial",
  vehicle: "Vehicle",
  equipment: "Equipment",
  financial_asset: "Financial",
};

const STATUS_COLORS: Record<string, string> = {
  identified: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  proposed: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  valued: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  approved: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  registered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  varied: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  substituted: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  discharged: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  cancelled: "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

// Format currency
const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const parseAmount = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  const num = typeof value === "number" ? value : parseFloat(value);
  return isNaN(num) ? 0 : num;
};

// Get short security reference
const getSecurityRef = (security: DealSecurity): string => {
  const typeAbbr = security.securityType?.slice(0, 3).toUpperCase() || "SEC";
  const idShort = security.id.slice(0, 6);
  return `${typeAbbr}-${idShort}`;
};

export function DealSecuritiesTab({ dealId }: DealSecuritiesTabProps) {
  const [, navigate] = useLocation();

  // Drawer state for security details
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createSecurityOpen, setCreateSecurityOpen] = useState(false);
  const [selectedSecurityId, setSelectedSecurityId] = useState<string | null>(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);

  // Fetch all securities for this deal (aggregated across facilities)
  const { data: securities = [], isLoading: isLoadingSecurities } = useQuery<DealSecurity[]>({
    queryKey: [`/api/crm/deals/${dealId}/securities`],
    enabled: !!dealId,
  });

  // Fetch deal security summary
  const { data: summary, isLoading: isLoadingSummary } = useQuery<DealSecuritySummary>({
    queryKey: [`/api/crm/deals/${dealId}/securities/summary`],
    enabled: !!dealId,
  });

  // Fetch facilities for this deal (to get facility names)
  const { data: facilities = [] } = useQuery<Facility[]>({
    queryKey: [`/api/crm/deals/${dealId}/facilities`],
    enabled: !!dealId,
  });

  // Create a map of facility IDs to names
  const facilityNameMap = useMemo(
    () =>
      facilities.reduce<Record<string, string>>((acc, facility) => {
        acc[facility.id] = facility.facilityName || `Facility ${facility.id.slice(0, 6)}`;
        return acc;
      }, {}),
    [facilities]
  );

  const securitiesForRegister = useMemo(
    () =>
      securities.map((security) => ({
        ...security,
        facilityName: security.facilityName ?? undefined,
      })),
    [securities],
  );

  const securitiesWithFacility = useMemo(
    () => {
      const withFacility: DealSecurityWithFacility[] = [];
      for (const security of securitiesForRegister) {
        if (!security.facilityId) continue;
        withFacility.push({
          ...security,
          facilityId: security.facilityId,
          facilityName: security.facilityName ?? undefined,
        });
      }
      return withFacility;
    },
    [securitiesForRegister],
  );

  const canonicalSecurities = useMemo<CanonicalDealSecurity<DealSecurity>[]>(
    () => buildCanonicalDealSecurities(securitiesForRegister, facilityNameMap),
    [facilityNameMap, securitiesForRegister],
  );

  const fallbackSummary = useMemo<DealSecuritySummary>(() => {
    const totalValue = canonicalSecurities.reduce(
      (sum, security) => sum + parseAmount(security.estimatedValue),
      0,
    );
    const totalFacilityLimit = facilities.reduce(
      (sum, facility) => sum + parseAmount(facility.approvedLimit || facility.requestedAmount),
      0,
    );
    const lvrPercentage = totalValue > 0 ? (totalFacilityLimit / totalValue) * 100 : null;

    return {
      totalValue,
      lvrPercentage,
      securityCount: canonicalSecurities.length,
      facilityCount: facilities.length,
      encumbranceCount: summary?.encumbranceCount ?? 0,
      totalEncumbranceValue: summary?.totalEncumbranceValue ?? 0,
    };
  }, [canonicalSecurities, facilities, summary?.encumbranceCount, summary?.totalEncumbranceValue]);

  const summaryIsBehind =
    canonicalSecurities.length > 0 &&
    (!summary ||
      summary.securityCount < canonicalSecurities.length ||
      summary.totalValue < fallbackSummary.totalValue);
  const displayedSummary = summaryIsBehind ? fallbackSummary : summary;

  // Open security detail drawer (instead of navigating away)
  const handleRowClick = (securityId: string, facilityId: string) => {
    setSelectedSecurityId(securityId);
    setSelectedFacilityId(facilityId);
    setDrawerOpen(true);
  };

  // Close drawer handler
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedSecurityId(null);
    setSelectedFacilityId(null);
  };

  // Navigate to facility with securities tab active (explicit action)
  const handleViewInFacility = (facilityId: string) => {
    navigate(`/facilities/${facilityId}#securities`);
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Summary Cards */}
        <DealSecuritiesSummary summary={displayedSummary} isLoading={isLoadingSummary && !displayedSummary} />

        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-sm">
            This view shows all securities across all facilities in this deal.
            Use <span className="font-medium">Add Security</span> to create one
            canonical record and link it to any facilities it secures. To edit
            or unlink securities, navigate to the specific{" "}
            <span className="font-medium">Facility &gt; Securities</span> tab.
          </AlertDescription>
        </Alert>

        {/* Section Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">All Securities ({canonicalSecurities.length})</h3>
          <Button
            type="button"
            size="sm"
            onClick={() => setCreateSecurityOpen(true)}
            data-testid="button-add-deal-security"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Security
          </Button>
        </div>

        {/* Securities Table */}
        {isLoadingSecurities ? (
          <Card>
            <CardContent className="pt-3">
              <p className="text-sm text-muted-foreground text-center py-8">
                Loading securities...
              </p>
            </CardContent>
          </Card>
        ) : securities.length === 0 ? (
          <Card>
            <CardContent className="pt-3">
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  No securities in this deal yet.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use <span className="font-medium">Add Security</span> to create a
                  canonical security and link it to the facilities it secures.
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  onClick={() => setCreateSecurityOpen(true)}
                  data-testid="button-add-deal-security-empty"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Security
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="h-8 bg-muted/50">
                    <TableHead className="px-2 py-1 text-xs font-medium w-[100px]">
                      Ref
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[90px]">
                      Type
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">
                      Description
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium text-right w-[100px]">
                      Value
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[120px]">
                      Facility
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[80px]">
                      Status
                    </TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[70px]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {canonicalSecurities.map((security) => {
                    const isCrossCollateral = security.linkedFacilities.length > 1;
                    const linkedFacilities = security.linkedFacilities.map((facility) => facility.facilityName);
                    const primaryFacilityId = security.primaryFacilityId ?? security.facilityId ?? null;
                    const primaryFacilityName = primaryFacilityId
                      ? security.facilityName ||
                        facilityNameMap[primaryFacilityId] ||
                        `Facility ${primaryFacilityId.slice(0, 6)}`
                      : null;

                    return (
                      <TableRow
                        key={`${security.id}-${primaryFacilityId ?? "unlinked"}`}
                        className={`h-8 hover:bg-muted/50 ${primaryFacilityId ? "cursor-pointer" : ""}`}
                        onClick={() => {
                          if (primaryFacilityId) handleRowClick(security.id, primaryFacilityId);
                        }}
                      >
                        <TableCell className="px-2 py-1">
                          <span className="text-xs font-mono text-primary flex items-center gap-1">
                            {isCrossCollateral && (
                              <Link2 className="h-3 w-3 text-amber-500 shrink-0" />
                            )}
                            {getSecurityRef(security)}
                          </span>
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <Badge variant="outline" className="text-xs">
                            {SECURITY_TYPE_LABELS[security.securityType] || security.securityType}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm max-w-[200px]">
                          <div className="flex flex-col gap-0.5">
                            <span className="truncate">{getSecurityDisplayName(security)}</span>
                            {isCrossCollateral && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className="text-xs w-fit bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-700 cursor-help"
                                    data-testid={`badge-cross-collateral-${security.id}`}
                                  >
                                    Cross-Collateral
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="max-w-[200px]"
                                  data-testid={`tooltip-linked-facilities-${security.id}`}
                                >
                                  <p className="text-xs font-medium mb-1">Also secured by:</p>
                                  <ul className="text-xs space-y-0.5">
                                    {linkedFacilities.map((name) => (
                                      <li key={name}>• {name}</li>
                                    ))}
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-right font-mono">
                          {formatCurrency(security.estimatedValue)}
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <Badge variant="secondary" className="text-xs">
                            {primaryFacilityName ?? "Unlinked"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <Badge
                            variant="secondary"
                            className={`text-xs capitalize ${
                              STATUS_COLORS[security.status ?? "identified"] || ""
                            }`}
                          >
                            {(security.status ?? "identified").replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={!primaryFacilityId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (primaryFacilityId) handleViewInFacility(primaryFacilityId);
                            }}
                            title={
                              primaryFacilityId
                                ? "View in Facility"
                                : "Link this security to a facility from the deal register first"
                            }
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Cross-Collateralisation Summary Card — hidden when no cross-collateral */}
        <CrossCollateralSummary
          securities={securitiesWithFacility}
          facilityNameMap={facilityNameMap}
        />

        {/* Security Detail Drawer - opens on row click */}
        <SecurityDetailDrawer
          open={drawerOpen}
          onOpenChange={(open) => {
            if (!open) handleDrawerClose();
          }}
          securityId={selectedSecurityId}
          facilityId={selectedFacilityId ?? ""}
        />
        <SecurityFormModal
          open={createSecurityOpen}
          onOpenChange={setCreateSecurityOpen}
          mode="deal"
          dealId={dealId}
        />
      </div>
    </TooltipProvider>
  );
}
